import {
    Component,
    OnInit,
    Input,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    ViewChild,
    ChangeDetectionStrategy,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25ModalModule } from "../../../s25-modal/s25.modal.module";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25Util } from "../../../../util/s25-util";
import { Budget } from "../Budget";
import { BudgetLabelPhrases, BudgetLabels } from "../budget.labels";
import { BudgetService } from "../budget.service";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25EditableModule } from "../../../s25-editable/s25.editable.module";
import { S25DropdownPaginatedModule } from "../../../s25-dropdown/s25.dropdown.paginated.module";
import { WorkflowService } from "../../../workflow/service/workflow.service";
import { Workflow } from "../../../workflow/service/Workflow";

@TypeManagerDecorator("s25-ng-budget-create-modal")
@Component({
    selector: "s25-ng-budget-create-modal",
    template: `
        <div>
            <button (click)="initModal()" class="create-budget-button">{{ labels.CREATE_ALLOCATION }}</button>
            <s25-ng-modal #budgetCreateModal [title]="labels.CREATE_ALLOCATION" [type]="'custom'" [size]="'lg'">
                <ng-template #s25ModalBody>
                    @if (showBudgetForm) {
                        <form [formGroup]="budgetForm" (ngSubmit)="onSubmit()" class="budget-create-form">
                            <!-- Organization Selection -->
                            <div class="form-section">
                                <h4>Organization</h4>
                                <div class="form-field">
                                    <label for="organization" class="required">Select Organization</label>
                                    <s25-ng-dropdown-search-criteria
                                        type="organizations"
                                        [(value)]="selectedOrganization"
                                        (valueChange)="onOrganizationChange($event)"
                                        placeholder="Select an Organization"
                                        class="organization-dropdown"
                                    />
                                    @if (
                                        budgetForm.get("organizationId")?.invalid &&
                                        budgetForm.get("organizationId")?.touched
                                    ) {
                                        <div class="validation-error">Please select an organization</div>
                                    }
                                </div>

                                <div class="form-field">
                                    <label for="workflowProcess" class="c-margin-top--single">Approval Workflow</label>
                                    <select id="workflowProcess" class="c-input" formControlName="workflowProcessId">
                                        <option [ngValue]="null">None (auto-approve plans)</option>
                                        @for (workflow of availableWorkflows; track workflow.workflowProcessId) {
                                            <option [ngValue]="workflow.workflowProcessId">
                                                {{ workflow.workflowProcessName }}
                                            </option>
                                        }
                                    </select>
                                    <p class="field-help">{{ labelPhrases.allocationWorkflowHelp }}</p>
                                </div>
                            </div>

                            <!-- Budget Details -->
                            <div class="form-section">
                                <h4>{{ labels.ALLOCATION_DETAILS }}</h4>

                                <div class="form-row">
                                    <div class="form-field">
                                        <label for="budgetName" class="required">{{ labels.ALLOCATION_NAME }}</label>
                                        <input
                                            type="text"
                                            id="budgetName"
                                            class="c-input"
                                            formControlName="name"
                                            [placeholder]="labelPhrases.enterAllocationName"
                                            maxlength="100"
                                        />
                                        @if (budgetForm.get("name")?.invalid && budgetForm.get("name")?.touched) {
                                            <div class="validation-error">
                                                @if (budgetForm.get("name")?.errors?.["required"]) {
                                                    {{ labelPhrases.allocationNameRequired }}
                                                }
                                                @if (budgetForm.get("name")?.errors?.["minlength"]) {
                                                    {{ labelPhrases.allocationNameMinLength }}
                                                }
                                            </div>
                                        }
                                    </div>

                                    <div class="form-field">
                                        <label for="fiscalYear" class="required">Fiscal Year</label>
                                        <select
                                            id="fiscalYear"
                                            class="c-input"
                                            formControlName="fiscalYearId"
                                            (change)="onFiscalYearChange($event)"
                                        >
                                            <option value="">Select Fiscal Year</option>
                                            @for (fiscalYear of activeFiscalYears; track fiscalYear.fiscalYearId) {
                                                <option [value]="fiscalYear.fiscalYearId">
                                                    {{ fiscalYear.name }}
                                                </option>
                                            }
                                        </select>
                                        @if (
                                            budgetForm.get("fiscalYearId")?.invalid &&
                                            budgetForm.get("fiscalYearId")?.touched
                                        ) {
                                            <div class="validation-error">Fiscal year is required</div>
                                        }
                                    </div>
                                </div>

                                <div class="form-field">
                                    <label for="description">Description</label>
                                    <textarea
                                        id="description"
                                        class="c-input"
                                        formControlName="description"
                                        [placeholder]="labelPhrases.enterAllocationDescription"
                                        rows="3"
                                        maxlength="500"
                                    ></textarea>
                                </div>
                            </div>

                            <!-- Budget Allocation -->
                            <div class="form-section">
                                <h4>{{ labels.ALLOCATION_ALLOCATION }}</h4>

                                <div class="form-row">
                                    <div class="form-field">
                                        <label for="totalAllocated" class="required">Allocated Amount</label>
                                        <input
                                            type="number"
                                            id="totalAllocated"
                                            class="c-input"
                                            formControlName="totalAllocated"
                                            placeholder="0.00"
                                            min="0"
                                            step="0.01"
                                        />
                                        @if (
                                            budgetForm.get("totalAllocated")?.invalid &&
                                            budgetForm.get("totalAllocated")?.touched
                                        ) {
                                            <div class="validation-error">
                                                @if (budgetForm.get("totalAllocated")?.errors?.["required"]) {
                                                    Allocated amount is required
                                                }
                                                @if (budgetForm.get("totalAllocated")?.errors?.["min"]) {
                                                    Amount must be greater than 0
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>

                                <div class="form-row">
                                    <div class="form-field">
                                        <label for="startDate" class="required">Start Date</label>
                                        <s25-ng-editable-date
                                            [(val)]="startDate"
                                            (valChange)="onStartDateChange($event)"
                                            [placeholder]="'Select start date'"
                                            [minDate]="minStartDate"
                                            [maxDate]="maxStartDate"
                                            class="date-input"
                                        ></s25-ng-editable-date>
                                        @if (
                                            budgetForm.get("startDate")?.invalid && budgetForm.get("startDate")?.touched
                                        ) {
                                            <div class="validation-error">Start date is required</div>
                                        }
                                    </div>

                                    <div class="form-field">
                                        <label for="endDate" class="required">End Date</label>
                                        <s25-ng-editable-date
                                            [(val)]="endDate"
                                            (valChange)="onEndDateChange($event)"
                                            [placeholder]="'Select end date'"
                                            [minDate]="minEndDate"
                                            [maxDate]="maxEndDate"
                                            class="date-input"
                                        ></s25-ng-editable-date>
                                        @if (budgetForm.get("endDate")?.invalid && budgetForm.get("endDate")?.touched) {
                                            <div class="validation-error">End date is required</div>
                                        }
                                    </div>
                                </div>

                                @if (budgetForm.errors?.["dateRange"]) {
                                    <div class="validation-error">Start date must be before end date</div>
                                }
                            </div>

                            <!-- Form Actions -->
                            <div class="form-actions">
                                <s25-ng-button
                                    [type]="'outline'"
                                    [buttonText]="'Cancel'"
                                    [onClick]="cancelForm"
                                ></s25-ng-button>
                                <s25-ng-button
                                    [type]="'primary'"
                                    [buttonText]="labels.CREATE_ALLOCATION"
                                    [onClick]="onSubmit"
                                    [isLoading]="isSubmitting"
                                    [loadingText]="'Creating...'"
                                    [disabled]="budgetForm.invalid"
                                ></s25-ng-button>
                            </div>
                        </form>
                    }
                </ng-template>
            </s25-ng-modal>
        </div>
    `,
    styles: `
        :host {
            display: inline-block;
        }

        .budget-create-form {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .form-section {
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 1.5rem;
            background: var(--color--page-bg);
        }

        .form-section h4 {
            margin: 0 0 1rem 0;
            color: var(--color--text);
            font-size: 1.1rem;
            font-weight: 600;
        }

        .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
            margin-bottom: 1rem;
        }

        .form-row:last-child {
            margin-bottom: 0;
        }

        .form-field {
            display: flex;
            flex-direction: column;
        }

        .form-field.full-width {
            grid-column: 1 / -1;
        }

        .form-field label {
            font-weight: 600;
            color: var(--color--text);
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
        }

        .form-field label.required::after {
            content: " *";
            color: var(--color--danger);
        }

        .form-field select {
            border: 1px solid var(--color--form-border);
            border-radius: 4px;
            font-size: 0.9rem;
            background: var(--color--form-bg);
            color: var(--color--text);
        }

        .form-field select:focus {
            outline: none;
            border-color: var(--cn-blue);
            box-shadow: var(--shadow--form-focus);
        }

        .validation-error {
            color: var(--color--danger);
            font-size: 0.8rem;
            margin-top: 0.25rem;
        }

        .field-help {
            margin: 0.5rem 0 0;
            color: var(--color--text-muted);
            font-size: 0.85rem;
            line-height: 1.4;
        }

        .form-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }

        .organization-dropdown {
            width: 100%;
        }

        .date-input {
            width: 100%;
        }

        .create-budget-button {
            background-color: var(--cn-blue);
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 4px;
            font-size: 1rem;
            font-weight: 500;
            cursor: pointer;
            transition: background-color var(--transition-medium);
        }

        .create-budget-button:hover {
            background-color: var(--color--link-text-hover);
        }

        @media (max-width: 768px) {
            .form-row {
                grid-template-columns: 1fr;
                gap: 1rem;
            }

            .form-actions {
                flex-direction: column;
                align-items: stretch;
            }

            .form-actions s25-ng-button {
                width: 100%;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25ModalModule, S25ButtonComponent, S25DropdownPaginatedModule, S25EditableModule, ReactiveFormsModule],
})
export class BudgetCreateModalComponent implements OnInit {
    readonly labels = BudgetLabels;
    readonly labelPhrases = BudgetLabelPhrases;

    @ViewChild("budgetCreateModal") budgetCreateModal: any;

    @Input() isOpen: boolean = false;
    @Output() budgetCreated = new EventEmitter<Budget.OrgBudget>();
    @Output() modalClosed = new EventEmitter<void>();

    showBudgetForm = false;
    isSubmitting = false;
    budgetForm: FormGroup;

    selectedOrganization: any = null;
    selectedFiscalYear: Budget.FiscalYear | null = null;
    activeFiscalYears: Budget.FiscalYear[] = [];
    availableWorkflows: Workflow.WorkflowProcess[] = [];
    startDate: Date | null = null;
    endDate: Date | null = null;

    minStartDate: Date = new Date();
    maxStartDate: Date = new Date(new Date().getFullYear() + 2, 11, 31);
    minEndDate: Date = new Date();
    maxEndDate: Date = new Date(new Date().getFullYear() + 2, 11, 31);

    constructor(
        private fb: FormBuilder,
        private cd: ChangeDetectorRef,
    ) {
        this.budgetForm = this.createForm();
    }

    async ngOnInit(): Promise<void> {
        await Promise.all([this.loadFiscalYears(), this.loadWorkflows()]);
        this.initializeForm();
    }

    private async loadWorkflows(): Promise<void> {
        try {
            this.availableWorkflows = await WorkflowService.getWorkflowProcesses(true);
        } catch (error) {
            console.error("Error loading workflow processes:", error);
            this.availableWorkflows = [];
        }
    }

    private async loadFiscalYears(): Promise<void> {
        try {
            const [fiscalYears, error] = await S25Util.Maybe(BudgetService.getFiscalYears());
            if (!error && fiscalYears) {
                this.activeFiscalYears = fiscalYears;
            }
        } catch (error) {
            console.error("Error loading fiscal years:", error);
        }
    }

    private createForm(): FormGroup {
        return this.fb.group(
            {
                organizationId: [null, Validators.required],
                name: ["", [Validators.required, Validators.minLength(3)]],
                description: [""],
                fiscalYearId: [null, Validators.required],
                totalAllocated: [0, [Validators.required, Validators.min(0.01)]],
                currency: ["USD"],
                startDate: [null, Validators.required],
                endDate: [null, Validators.required],
                workflowProcessId: [null],
            },
            { validators: this.dateRangeValidator },
        );
    }

    private dateRangeValidator(form: FormGroup) {
        const startDate = form.get("startDate")?.value;
        const endDate = form.get("endDate")?.value;

        if (startDate && endDate && startDate >= endDate) {
            return { dateRange: true };
        }
        return null;
    }

    private initializeForm(): void {
        // Try to select current fiscal year by default
        const currentFiscalYear = this.activeFiscalYears.find((fy) => fy.isCurrent);
        if (currentFiscalYear) {
            this.budgetForm.patchValue({
                fiscalYearId: currentFiscalYear.fiscalYearId,
            });
            this.onFiscalYearChange({ target: { value: currentFiscalYear.fiscalYearId } } as any);
        }
    }

    onOrganizationChange(organization: any): void {
        this.selectedOrganization = organization;
        this.budgetForm.patchValue({
            organizationId: organization?.itemId || null,
        });
    }

    onFiscalYearChange(event: any): void {
        const fiscalYearId = parseInt(event.target.value, 10);
        this.selectedFiscalYear = this.activeFiscalYears.find((fy) => fy.fiscalYearId === fiscalYearId) || null;

        if (this.selectedFiscalYear) {
            // Auto-populate dates from fiscal year
            const startDate = new Date(this.selectedFiscalYear.startDate);
            const endDate = new Date(this.selectedFiscalYear.endDate);

            this.startDate = startDate;
            this.endDate = endDate;

            this.budgetForm.patchValue({
                startDate: startDate,
                endDate: endDate,
            });

            this.cd.detectChanges();
        }
    }

    onStartDateChange(date: Date): void {
        this.startDate = date;
        this.budgetForm.patchValue({ startDate: date });

        // Update minimum end date
        if (date) {
            this.minEndDate = S25Util.date.addDays(new Date(), 1);
        }
    }

    onEndDateChange(date: Date): void {
        this.endDate = date;
        this.budgetForm.patchValue({ endDate: date });
    }

    @Bind
    async onSubmit(): Promise<void> {
        if (this.budgetForm.invalid) {
            this.markFormGroupTouched();
            return;
        }

        this.isSubmitting = true;
        this.cd.detectChanges();

        try {
            const formValue = this.budgetForm.value;
            const budgetData: any = {
                name: formValue.name,
                description: formValue.description,
                fiscalYearId: formValue.fiscalYearId,
                totalAllocated: formValue.totalAllocated,
                currency: formValue.currency,
                startDate: S25Util.date.toS25ISODateTimeStr(formValue.startDate),
                endDate: S25Util.date.toS25ISODateTimeStr(formValue.endDate),
                organizationId: formValue.organizationId,
                workflowProcessId: formValue.workflowProcessId ?? null,
            };

            const [result, error] = await S25Util.Maybe(
                BudgetService.createOrgBudget(formValue.organizationId, budgetData),
            );

            if (error) {
                throw new Error(S25Util.errorText(error));
            }

            this.budgetCreated.emit(result);
            this.closeModal();
        } catch (error) {
            S25Util.showError(`Failed to create budget: ${S25Util.errorText(error)}`);
        } finally {
            this.isSubmitting = false;
            this.cd.detectChanges();
        }
    }

    cancelForm = () => {
        this.closeModal();
    };

    private markFormGroupTouched(): void {
        Object.keys(this.budgetForm.controls).forEach((key) => {
            const control = this.budgetForm.get(key);
            control?.markAsTouched();
        });
    }

    openModal(): void {
        this.showBudgetForm = true;
        this.resetForm();
        this.budgetCreateModal?.open();
    }

    closeModal(): void {
        this.showBudgetForm = false;
        this.budgetCreateModal?.close();
        this.modalClosed.emit();
    }

    private resetForm(): void {
        this.budgetForm.reset();
        this.selectedOrganization = null;
        this.startDate = null;
        this.endDate = null;
        this.initializeForm();
    }

    initModal(): void {
        this.openModal();
    }
}
